import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Eye, 
  User,
  Mail,
  Phone,
  CheckCircle,
  Briefcase
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { apiService } from '../../services/apiService';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import toast from 'react-hot-toast';
import Modal from '../../components/UI/Modal';
import Input from '../../components/UI/Input';
import { formatDateTime } from '../../utils/formatDateTime';
import { useSettings } from '../../contexts/SettingsContext';
import Button from '../../components/UI/Button';

interface UserData {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  role: 'admin' | 'manager' | 'user' | 'requisition_user';
  isActive: boolean;
  permissions: string[];
  lastLogin?: string;
  createdAt: string;
  updatedAt: string;
  username?: string;
  department?: string;
  designation?: string;
}

const UsersPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [newUser, setNewUser] = useState({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    password: '',
    role: 'user',
    isActive: true,
    phone: '',
    department: '',
    designation: '',
  });
  const [showViewUserModal, setShowViewUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const { settings } = useSettings();

  useEffect(() => {
    fetchUsers();
    
    // Set up periodic refresh every 30 seconds
    const interval = setInterval(() => {
      fetchUsers();
    }, 30000);
    
    // Cleanup interval on component unmount
    return () => clearInterval(interval);
  }, []);

  // Soft refresh listener
  useEffect(() => {
    const onRefresh = () => {
      apiService.invalidateUsersCache();
      fetchUsers();
    };
    window.addEventListener('app:refresh', onRefresh as any);
    return () => window.removeEventListener('app:refresh', onRefresh as any);
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/users');
      setUsers(Array.isArray(response.data.users) ? response.data.users : (Array.isArray(response.data) ? response.data : []));
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await apiService.delete(`/users/${userId}`);
        toast.success('User deleted successfully');
        
        // Immediately clear users cache and force refresh
        apiService.invalidateUsersCache();
        
        // Force refresh to get updated data with a small delay to ensure cache is cleared
        setTimeout(async () => {
          await fetchUsers();
        }, 100);
      } catch (error) {
        console.error('Error deleting user:', error);
        toast.error('Failed to delete user');
      }
    }
  };

  const handleStatusChange = async (userId: string, newStatus: boolean) => {
    try {
      await apiService.put(`/users/${userId}`, { isActive: newStatus });
      toast.success('User status updated successfully');
      
      // Immediately clear users cache and force refresh
      apiService.invalidateUsersCache();
      
      // Force refresh to get updated data with a small delay to ensure cache is cleared
      setTimeout(async () => {
        await fetchUsers();
      }, 100);
    } catch (error) {
      console.error('Error updating user status:', error);
      toast.error('Failed to update user status');
    }
  };

  const handleRoleChange = async (userId: string, newRole: string) => {
    try {
      await apiService.put(`/users/${userId}`, { role: newRole }); // Changed from patch to put
      toast.success('User role updated successfully');
      
      // Immediately clear users cache and force refresh
      apiService.invalidateUsersCache();
      
      // Force refresh to get updated data with a small delay to ensure cache is cleared
      setTimeout(async () => {
        await fetchUsers();
      }, 100);
    } catch (error) {
      console.error('Error updating user role:', error);
      toast.error('Failed to update user role');
    }
  };

  const handleChangePassword = async () => {
    try {
      // Validate passwords
      if (!newPassword.trim()) {
        toast.error('New password is required');
        return;
      }
      if (newPassword.length < 6) {
        toast.error('Password must be at least 6 characters long');
        return;
      }
      if (newPassword !== confirmPassword) {
        toast.error('Passwords do not match');
        return;
      }

      if (!selectedUser) {
        toast.error('No user selected');
        return;
      }

      await apiService.patch(`/users/${selectedUser._id}/password`, { newPassword });
      toast.success('Password changed successfully');
      setShowChangePasswordModal(false);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      console.error('Error changing password:', error);
      if (error.response?.data?.errors) {
        // Display validation errors
        const errorMessages = error.response.data.errors.map((err: any) => err.msg).join(', ');
        toast.error(`Validation error: ${errorMessages}`);
      } else if (error.response?.data?.error) {
        // Display single error message
        toast.error(error.response.data.error);
      } else {
        toast.error('Failed to change password');
      }
    }
  };

  const handleAddUser = async () => {
    try {
      // Validate required fields
      if (!newUser.firstName.trim()) {
        toast.error('First name is required');
        return;
      }
      if (!newUser.lastName.trim()) {
        toast.error('Last name is required');
        return;
      }
      if (!newUser.username.trim()) {
        toast.error('Username is required');
        return;
      }
      if (!newUser.email.trim()) {
        toast.error('Email is required');
        return;
      }
      if (!newUser.password.trim()) {
        toast.error('Password is required');
        return;
      }
      
      // Only send fields that the backend expects
      const userData = {
        firstName: newUser.firstName,
        lastName: newUser.lastName,
        username: newUser.username,
        email: newUser.email,
        password: newUser.password,
        role: newUser.role,
        phone: newUser.phone || undefined,
        department: newUser.department || undefined,
        designation: newUser.designation || undefined
      };
      
      await apiService.post('/users', userData);
      toast.success('User added successfully');
      setShowAddUserModal(false);
      setNewUser({ firstName: '', lastName: '', username: '', email: '', password: '', role: 'user', isActive: true, phone: '', department: '', designation: '' });
      
      // Immediately clear users cache and force refresh
      apiService.invalidateUsersCache();
      
      // Force refresh to get updated data with a small delay to ensure cache is cleared
      setTimeout(async () => {
        await fetchUsers();
      }, 100);
    } catch (error: any) {
      console.error('Error adding user:', error);
      if (error.response?.data?.errors) {
        // Display validation errors
        const errorMessages = error.response.data.errors.map((err: any) => err.msg).join(', ');
        toast.error(`Validation error: ${errorMessages}`);
      } else if (error.response?.data?.error) {
        // Display single error message
        toast.error(error.response.data.error);
      } else {
        toast.error('Failed to add user');
      }
    }
  };

  const filteredUsers = (Array.isArray(users) ? users : []).filter(user => {
    const matchesSearch = user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = !selectedRole || user.role === selectedRole;
    const matchesStatus = !selectedStatus || user.isActive === (selectedStatus === 'true');
    
    return matchesSearch && matchesRole && matchesStatus;
  });

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'bg-[var(--color-primary-light)] text-[var(--color-primary)]';
      case 'manager': return 'bg-[var(--color-secondary-light)] text-[var(--color-secondary)]';
      case 'user': return 'bg-[var(--color-success-light)] text-[var(--color-success)]';
      case 'requisition_user': return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300';
      default: return 'bg-[var(--color-surface)] text-[var(--color-text-secondary)]';
    }
  };

  const getStatusColor = (isActive: boolean) => {
    switch (isActive) {
      case true: return 'bg-[var(--color-success-light)] text-[var(--color-success)]';
      case false: return 'bg-[var(--color-surface)] text-[var(--color-text-secondary)]';
      default: return 'bg-[var(--color-surface)] text-[var(--color-text-secondary)]';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 font-[var(--font-family)] text-[var(--color-text)]">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[var(--color-text)] font-[var(--font-family)]">Users</h1>
          <p className="text-[var(--color-text-secondary)] font-[var(--font-family)]">
            Manage system users, roles, and permissions
          </p>
        </div>
        <div className="flex space-x-3">
          <Button
            variant="primary"
            onClick={() => setShowAddUserModal(true)}
            icon={<Plus className="w-4 h-4 mr-2" />}
          >
            Add User
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-[var(--color-surface)] rounded-lg shadow-md p-6" style={{ borderRadius: 'var(--border-radius)' } as unknown as React.CSSProperties}>
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--color-text-secondary)] w-4 h-4" />
              <input
                type="text"
                placeholder="Search users by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman']"
                style={{ borderRadius: 'var(--border-radius)', background: 'var(--color-surface)', color: 'var(--color-text)' } as unknown as React.CSSProperties}
              />
            </div>
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center px-4 py-2 bg-[var(--color-background)] text-[var(--color-text)] rounded-lg hover:bg-[var(--color-surface)] transition-colors font-['Times_New_Roman']"
            style={{ borderRadius: 'var(--border-radius)' } as unknown as React.CSSProperties}
          >
            <Filter className="w-4 h-4 mr-2" />
            Filters
          </button>
        </div>

        {/* Filter Options */}
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 } as any}
            animate={{ opacity: 1, height: 'auto' } as any}
            exit={{ opacity: 0, height: 0 } as any}
            className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4"
          >
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman']"
              style={{ borderRadius: 'var(--border-radius)' } as unknown as React.CSSProperties}
            >
              <option value="">All Roles</option>
              <option value="admin">Admin</option>
              <option value="manager">Manager</option>
              <option value="user">User</option>
              <option value="requisition_user">Requisition User</option>
            </select>

            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-3 py-2 border border-[var(--color-border)] rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent font-['Times_New_Roman']"
              style={{ borderRadius: 'var(--border-radius)' } as unknown as React.CSSProperties}
            >
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </motion.div>
        )}
      </div>

      {/* Users Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredUsers.map((user) => (
          <motion.div
            key={user._id}
            initial={{ opacity: 0, y: 20 } as any}
            animate={{ opacity: 1, y: 0 } as any}
            className="bg-[var(--color-surface)] rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow"
            style={{ borderRadius: 'var(--border-radius)' } as unknown as React.CSSProperties}
          >
            {/* User Header */}
            <div className="p-6 border-b border-[var(--color-border)]">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-[var(--color-primary)] rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-white" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-[var(--color-text)] font-['Times_New_Roman']">
                      {user.firstName} {user.lastName}
                    </h3>
                    <p className="text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman']">{user.email}</p>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <select
                    value={user.role}
                    onChange={(e) => handleRoleChange(user._id, e.target.value)}
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium pr-8 ${getRoleColor(user.role)} font-['Times_New_Roman']`}
                    style={{ borderRadius: 'var(--border-radius)' } as unknown as React.CSSProperties}
                  >
                    <option value="admin">Admin</option>
                    <option value="manager">Manager</option>
                    <option value="user">User</option>
                    <option value="requisition_user">Requisition User</option>
                  </select>
                  <select
                    value={user.isActive ? 'true' : 'false'}
                    onChange={(e) => handleStatusChange(user._id, e.target.value === 'true')}
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium pr-8 ${getStatusColor(user.isActive)} font-['Times_New_Roman']`}
                    style={{ borderRadius: 'var(--border-radius)' } as unknown as React.CSSProperties}
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              </div>
            </div>

            {/* User Details */}
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 gap-3 text-sm font-['Times_New_Roman']">
                <div className="flex items-center text-[var(--color-text-secondary)]">
                  <Mail className="w-4 h-4 mr-3" />
                  <span>{user.email}</span>
                </div>
                {user.phone && (
                  <div className="flex items-center text-[var(--color-text-secondary)]">
                    <Phone className="w-4 h-4 mr-3" />
                    <span>{user.phone}</span>
                  </div>
                )}
                {user.designation && (
                  <div className="flex items-center text-[var(--color-text-secondary)]">
                    <Briefcase className="w-4 h-4 mr-3" />
                    <span>{user.designation}</span>
                  </div>
                )}
                <div className="flex items-center text-[var(--color-text-secondary)]">
                  <CheckCircle className="w-4 h-4 mr-3" />
                  <span>Joined: {formatDateTime(user.createdAt, settings)}</span>
                </div>
                {user.lastLogin && (
                  <div className="flex items-center text-[var(--color-text-secondary)]">
                    <CheckCircle className="w-4 h-4 mr-3" />
                    <span>Last login: {formatDateTime(user.lastLogin, settings)}</span>
                  </div>
                )}
              </div>

              {/* Permissions */}
              <div>
                <h4 className="text-sm font-medium text-[var(--color-text)] mb-2 font-['Times_New_Roman']">Permissions</h4>
                <div className="flex flex-wrap gap-1">
                  {(Array.isArray(user.permissions) ? user.permissions : []).slice(0, 3).map((permission, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-[var(--color-primary-light)] text-[var(--color-primary)] font-['Times_New_Roman']"
                      style={{ borderRadius: 'var(--border-radius)' } as unknown as React.CSSProperties}
                    >
                      {permission}
                    </span>
                  ))}
                  {(Array.isArray(user.permissions) ? user.permissions : []).length > 3 && (
                    <span
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-[var(--color-surface)] text-[var(--color-text-secondary)] font-['Times_New_Roman']"
                      style={{ borderRadius: 'var(--border-radius)' } as unknown as React.CSSProperties}
                    >
                      +{(Array.isArray(user.permissions) ? user.permissions : []).length - 3} more
                    </span>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t border-[var(--color-border)]">
                <div className="flex space-x-2">
                  <button
                    className="p-2 text-[var(--color-primary)] hover:bg-[var(--color-primary-light)] rounded-lg transition-colors"
                    title="View Details"
                    onClick={() => { setSelectedUser(user); setShowViewUserModal(true); }}
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    className="p-2 text-[var(--color-success)] hover:bg-[var(--color-success-light)] rounded-lg transition-colors"
                    title="Edit User"
                    onClick={() => { setSelectedUser(user); setShowEditUserModal(true); }}
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    className="p-2 text-[var(--color-warning)] hover:bg-[var(--color-warning-light)] rounded-lg transition-colors"
                    title="Change Password"
                    onClick={() => { setSelectedUser(user); setShowChangePasswordModal(true); }}
                  >
                    <User className="w-4 h-4" />
                  </button>
                </div>
                {user._id !== currentUser?._id && (
                  <button
                    onClick={() => handleDeleteUser(user._id)}
                    className="p-2 text-[var(--color-danger)] hover:bg-[var(--color-danger-light)] rounded-lg transition-colors"
                    title="Delete User"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Empty State */}
      {filteredUsers.length === 0 && (
        <div className="text-center py-12">
          <User className="w-16 h-16 text-[var(--color-text-secondary)] mx-auto mb-4" />
          <h3 className="text-lg font-medium text-[var(--color-text)] mb-2 font-['Times_New_Roman']">
            No users found
          </h3>
          <p className="text-[var(--color-text-secondary)] font-['Times_New_Roman']">
            {searchTerm || selectedRole || selectedStatus
              ? 'Try adjusting your search or filters'
              : 'Get started by adding your first user'
            }
          </p>
        </div>
      )}

      {/* Results Count */}
      <div className="text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman']">
        Showing {filteredUsers.length} of {users.length} users
      </div>

      <Modal isOpen={showAddUserModal} onClose={() => setShowAddUserModal(false)} title="Add User" size="xl" className="!max-w-7xl">
        <div className="space-y-4">
          <Input label="First Name *" value={newUser.firstName} onChange={e => setNewUser({ ...newUser, firstName: e.target.value })} required />
          <Input label="Last Name *" value={newUser.lastName} onChange={e => setNewUser({ ...newUser, lastName: e.target.value })} required />
          <Input label="Username *" value={newUser.username} onChange={e => setNewUser({ ...newUser, username: e.target.value })} required />
          <Input label="Email *" value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value })} required />
          <Input label="Password *" type="password" value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })} required />
          <Input label="Phone" value={newUser.phone} onChange={e => setNewUser({ ...newUser, phone: e.target.value })} />
          <Input label="Department" value={newUser.department} onChange={e => setNewUser({ ...newUser, department: e.target.value })} />
          <Input label="Designation" value={newUser.designation} onChange={e => setNewUser({ ...newUser, designation: e.target.value })} />
          <select value={newUser.role} onChange={e => setNewUser({ ...newUser, role: e.target.value })} className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-surface)] text-[var(--color-text)] font-['Times_New_Roman'] focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent">
            <option value="admin">Admin</option>
            <option value="manager">Manager</option>
            <option value="user">User</option>
            <option value="requisition_user">Requisition User</option>
          </select>
          <select value={newUser.isActive ? 'true' : 'false'} onChange={e => setNewUser({ ...newUser, isActive: e.target.value === 'true' })} className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-surface)] text-[var(--color-text)] font-['Times_New_Roman'] focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent">
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
          <button className="btn btn-primary w-full" onClick={handleAddUser}>Add User</button>
        </div>
      </Modal>
      <Modal isOpen={showViewUserModal} onClose={() => setShowViewUserModal(false)} title="User Details" size="xl" className="!max-w-7xl">
        {selectedUser && (
          <div className="space-y-2">
            <div><b>Name:</b> {selectedUser.firstName} {selectedUser.lastName}</div>
            <div><b>Username:</b> {selectedUser.username || '-'}</div>
            <div><b>Email:</b> {selectedUser.email}</div>
            <div><b>Phone:</b> {selectedUser.phone || '-'}</div>
            <div><b>Department:</b> {selectedUser.department || '-'}</div>
            <div><b>Designation:</b> {selectedUser.designation || '-'}</div>
            <div><b>Role:</b> {selectedUser.role === 'requisition_user' ? 'Requisition User' : selectedUser.role}</div>
            <div><b>Status:</b> {selectedUser.isActive ? 'Active' : 'Inactive'}</div>
            <div><b>Permissions:</b> {Array.isArray(selectedUser.permissions) ? selectedUser.permissions.join(', ') : '-'}</div>
            <div><b>Joined:</b> {formatDateTime(selectedUser.createdAt, settings)}</div>
            {selectedUser.lastLogin && <div><b>Last Login:</b> {formatDateTime(selectedUser.lastLogin, settings)}</div>}
          </div>
        )}
      </Modal>
      <Modal isOpen={showEditUserModal} onClose={() => setShowEditUserModal(false)} title="Edit User" size="xl" className="!max-w-7xl">
        {selectedUser && (
          <div className="space-y-4">
            <Input label="First Name" value={selectedUser.firstName} onChange={e => setSelectedUser({ ...selectedUser, firstName: e.target.value })} />
            <Input label="Last Name" value={selectedUser.lastName} onChange={e => setSelectedUser({ ...selectedUser, lastName: e.target.value })} />
            <Input label="Email" value={selectedUser.email} onChange={e => setSelectedUser({ ...selectedUser, email: e.target.value })} />
            <Input label="Username" value={selectedUser.username || ''} onChange={e => setSelectedUser({ ...selectedUser, username: e.target.value })} />
            <Input label="Phone" value={selectedUser.phone || ''} onChange={e => setSelectedUser({ ...selectedUser, phone: e.target.value })} />
            <Input label="Department" value={selectedUser.department || ''} onChange={e => setSelectedUser({ ...selectedUser, department: e.target.value })} />
            <Input label="Designation" value={selectedUser.designation || ''} onChange={e => setSelectedUser({ ...selectedUser, designation: e.target.value })} />
            <select value={selectedUser.role} onChange={e => setSelectedUser({ ...selectedUser, role: e.target.value as UserData['role'] })} className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-surface)] text-[var(--color-text)] font-['Times_New_Roman'] focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent">
              <option value="admin">Admin</option>
              <option value="manager">Manager</option>
              <option value="user">User</option>
              <option value="requisition_user">Requisition User</option>
            </select>
            <select value={selectedUser.isActive ? 'true' : 'false'} onChange={e => setSelectedUser({ ...selectedUser, isActive: e.target.value === 'true' })} className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-surface)] text-[var(--color-text)] font-['Times_New_Roman'] focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent">
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
            <button className="btn btn-primary w-full" onClick={async () => {
              try {
                await apiService.put(`/users/${selectedUser._id}`, {
                  username: selectedUser.username,
                  email: selectedUser.email,
                  firstName: selectedUser.firstName,
                  lastName: selectedUser.lastName,
                  role: selectedUser.role,
                  phone: selectedUser.phone,
                  department: selectedUser.department,
                  designation: selectedUser.designation,
                  permissions: selectedUser.permissions,
                  isActive: selectedUser.isActive,
                });
                toast.success('User updated successfully');
                setShowEditUserModal(false);
                
                // Immediately clear users cache and force refresh
                apiService.invalidateUsersCache();
                
                // Force refresh to get updated data with a small delay to ensure cache is cleared
                setTimeout(async () => {
                  await fetchUsers();
                }, 100);
              } catch (error: any) {
                console.error('Error updating user:', error);
                if (error.response?.data?.errors) {
                  // Display validation errors
                  const errorMessages = error.response.data.errors.map((err: any) => err.msg).join(', ');
                  toast.error(`Validation error: ${errorMessages}`);
                } else if (error.response?.data?.error) {
                  // Display single error message
                  toast.error(error.response.data.error);
                } else {
                  toast.error('Failed to update user');
                }
              }
            }}>Save Changes</button>
          </div>
        )}
      </Modal>
      <Modal isOpen={showChangePasswordModal} onClose={() => setShowChangePasswordModal(false)} title="Change Password" size="xl" className="!max-w-7xl">
        {selectedUser && (
          <div className="space-y-4">
            <div className="text-sm text-[var(--color-text-secondary)] font-['Times_New_Roman']">
              Changing password for: <strong>{selectedUser.firstName} {selectedUser.lastName}</strong>
            </div>
            <Input 
              label="New Password *" 
              type="password" 
              value={newPassword} 
              onChange={e => setNewPassword(e.target.value)} 
              required 
            />
            <Input 
              label="Confirm Password *" 
              type="password" 
              value={confirmPassword} 
              onChange={e => setConfirmPassword(e.target.value)} 
              required 
            />
            <button className="btn btn-primary w-full" onClick={handleChangePassword}>Change Password</button>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default UsersPage; 